import { useEffect, useState } from "react";
import { PhoneIncoming, PhoneOutgoing, MessageSquare } from "lucide-react";
import { apiGet } from "@/lib/apiAdmin";
import { HUD, timeAgo } from "./theme";

const TypeChip = ({ t }) => (
  <span className={`${HUD.mono} text-[9px] tracking-[0.2em] px-2 py-0.5 rounded-full border uppercase ${t === "team" ? "border-violet-400/40 text-violet-300" : "border-cyan-400/40 text-cyan-300"}`}>
    {t === "team" ? "team" : "potential customer"}
  </span>
);

const CallRow = ({ c }) => {
  const [open, setOpen] = useState(false);
  const Icon = c.direction === "inbound" ? PhoneIncoming : PhoneOutgoing;
  return (
    <div className="p-4 space-y-1" data-testid={`jarvis-call-${c.call_id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Icon className="w-4 h-4 text-cyan-300" />
        <p className="text-sm text-white">{c.name || c.number || "Unknown"}</p>
        <TypeChip t={c.caller_type} />
        <span className={`${HUD.mono} text-[10px] text-gray-500 uppercase`}>{c.status} · {timeAgo(c.ended_at || c.created_at)}</span>
      </div>
      {c.objective && <p className="text-xs text-gray-400">Objective: {c.objective}</p>}
      {c.summary && <p className="text-xs text-gray-300">{c.summary}</p>}
      {c.error && <p className="text-xs text-red-300">{c.error}</p>}
      {c.recording_url && <audio controls src={c.recording_url} className="w-full h-8" data-testid={`jarvis-call-recording-${c.call_id}`} />}
      {c.transcript && (
        <button onClick={() => setOpen(!open)} className="text-[11px] text-cyan-300 hover:underline">{open ? "Hide" : "Show"} transcript</button>
      )}
      {open && <pre className="text-[11px] text-gray-400 whitespace-pre-wrap bg-black/40 rounded-lg p-3 max-h-60 overflow-y-auto">{c.transcript}</pre>}
    </div>
  );
};

export const JarvisComms = () => {
  const [calls, setCalls] = useState([]);
  const [inbox, setInbox] = useState([]);
  useEffect(() => {
    apiGet("/admin/jarvis/calls").then((d) => setCalls(d.items || [])).catch(() => {});
    apiGet("/admin/jarvis/inbox").then((d) => setInbox(d.items || [])).catch(() => {});
  }, []);
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="jarvis-comms">
      <div className={`${HUD.panel} divide-y divide-cyan-400/5`}>
        <p className={`${HUD.label} p-4`}>Calls</p>
        {calls.length === 0 ? <p className="p-4 text-sm text-gray-500">No calls yet.</p> : calls.map((c) => <CallRow key={c.call_id} c={c} />)}
      </div>
      <div className={`${HUD.panel} divide-y divide-cyan-400/5`}>
        <p className={`${HUD.label} p-4`}>Inbound texts</p>
        {inbox.length === 0 ? (
          <p className="p-4 text-sm text-gray-500">No texts yet. Connect the OpenPhone webhook in Settings.</p>
        ) : (
          inbox.map((m) => (
            <div key={m.inbox_id} className="p-4 space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <MessageSquare className="w-4 h-4 text-cyan-300" />
                <p className="text-sm text-white">{m.name || m.from}</p>
                <TypeChip t={m.sender_type} />
                <span className={`${HUD.mono} text-[10px] text-gray-500`}>{timeAgo(m.created_at)}</span>
              </div>
              <p className="text-xs text-gray-300 break-words">{m.text}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default JarvisComms;
